import { ArrowRight, Star, Phone, MapPin, Sparkles } from "lucide-react";
import { HERO_IMG } from "@/lib/mock";

export default function Hero() {
  const openVisualizer = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("open-visualizer"));
    }
  };
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 bg-stone-50 overflow-hidden"
    >
      {/* Soft orange glow */}
      <div
        aria-hidden
        className="absolute -top-32 -right-20 w-[700px] h-[700px] rounded-full opacity-[0.12] blur-[140px] pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(234,88,12,0.9) 0%, rgba(234,88,12,0) 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT */}
          <div className="lg:col-span-6 animate-fade-up">
            <div className="inline-flex items-center gap-2.5 bg-stone-900/5 px-4 py-2 rounded-full text-xs font-semibold text-stone-700 mb-7">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse-dot" />
              <MapPin size={12} strokeWidth={2.5} className="text-orange-600" />
              Greater St. Louis · St. Charles County
            </div>

            <h1 className="font-display font-semibold text-[52px] sm:text-[68px] lg:text-[88px] xl:text-[100px] leading-[0.95] tracking-[-0.03em] text-stone-900">
              Premium <span className="text-orange-600">flooring installation</span>,
              <br />
              built to last.
            </h1>

            <p className="mt-8 max-w-xl text-lg lg:text-xl leading-relaxed text-stone-600">
              LVP, laminate, and tile — installed with obsessive precision
              across Greater St. Louis. Hand-built by owner-operators. Every
              subfloor prepped, every seam tight, every install built to last.
            </p>

            {/* Hero discount pill */}
            <div
              data-testid="hero-discount-pill"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-sm font-semibold"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-600 text-white text-xs font-bold">
                %
              </span>
              Ask about our 20% discount on materials
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-stone-900 text-stone-50 font-semibold rounded-full hover:bg-orange-600 transition-all duration-300"
              >
                Get a Free Quote
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
              <button
                type="button"
                onClick={openVisualizer}
                data-testid="hero-cta-visualize"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-white border-2 border-orange-500 text-orange-700 font-semibold rounded-full hover:bg-orange-500 hover:text-stone-50 transition-all duration-300"
              >
                <Sparkles size={16} strokeWidth={2.25} />
                Visualize in Your Room
              </button>
              <a
                href="tel:+16368496681"
                data-testid="hero-cta-phone"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white border border-stone-200 text-stone-900 font-semibold rounded-full hover:border-stone-900 hover:bg-stone-50 transition-all"
              >
                <Phone size={16} strokeWidth={2.25} />
                636-849-6681
              </a>
            </div>

            {/* Roomvo help text */}
            <p className="mt-4 text-xs text-stone-500 max-w-md">
              <span className="font-semibold text-orange-600">New:</span> Snap a
              photo of your room and see any LVP, laminate, or tile in your
              actual space — right on your phone. Free, no signup.
            </p>

            {/* Proof row */}
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={16} className="fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <span className="text-sm font-semibold text-stone-900">5.0</span>
                <span className="text-sm text-stone-500">· 120+ reviews</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-stone-300" />
              <div className="text-sm text-stone-600">
                <span className="font-semibold text-stone-900">12+ years</span>{" "}
                in business
              </div>
              <div className="hidden sm:block h-5 w-px bg-stone-300" />
              <div className="text-sm text-stone-600">
                <span className="font-semibold text-stone-900">Licensed</span> &
                Insured
              </div>
            </div>
          </div>

          {/* RIGHT — image */}
          <div className="lg:col-span-6 animate-fade-up stagger-2">
            <div className="relative">
              <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden rounded-3xl bg-stone-200">
                <img
                  src={HERO_IMG}
                  alt="M&L Exterior and Interior Design — Premium marble-look tile flooring in luxury living room"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width="800"
                  height="1000"
                  data-testid="hero-image"
                  className="absolute inset-0 h-full w-full object-cover animate-slow-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/30 via-transparent to-transparent" />
              </div>

              {/* Floating stat card */}
              <div
                data-testid="hero-floating-card"
                className="hidden md:flex absolute -left-6 lg:-left-10 bottom-10 bg-white rounded-2xl p-5 shadow-[0_24px_60px_-20px_rgba(28,25,23,0.3)] items-center gap-4"
              >
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-stone-50 font-display text-xl font-bold">
                  12
                </div>
                <div>
                  <div className="font-semibold text-stone-900 leading-none">
                    Years building
                  </div>
                  <div className="text-xs text-stone-500 mt-1">
                    240+ projects across MO
                  </div>
                </div>
              </div>

              {/* Featured chip */}
              <div className="hidden lg:flex absolute top-6 right-6 items-center gap-2 bg-stone-900/85 backdrop-blur-md text-stone-50 px-4 py-2 rounded-full text-xs font-semibold">
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
                Featured Project
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
